/**
 * Article-unit mock adapter (article-unit pivot W1 · design §2.8).
 *
 * The unit of work is an ARTICLE UNIT = { brief, research, article } — three
 * role-tagged `el.documents` rows under one `el.article_units` aggregate
 * (design §0/§1). This module is the typed, backend-free seam the
 * `<ArticleWorkbench>` renders against: it produces the exact `ArticleUnit` /
 * `Section` / `SectionVersion` / `VersionTick` / `DocumentDiff` shapes the real
 * `/api/article-units` endpoints will return, seeded with real Wettformat
 * content so the whole workbench renders + is testable with NO backend.
 *
 * RULE ZERO: the diff shape is the upstream `docz_diff.DocumentDiff` re-exported
 * from `@norkron/web-review-workbench` — the SAME type the Zig→WASM docz core
 * emits — so the client never owns a second diff model. `VersionTick` is the
 * workbench's own timeline contract. The real adapter (fetch `/api`) is wired at
 * W1 integration by swapping the one `articleApi` export at the bottom.
 */

import type {
  DocumentDiff,
  Kind,
  VersionTick,
} from "@norkron/web-review-workbench";
import { encodeBlockList } from "@norkron/web-docz";
import { hashQuote } from "../lib/anchoring";
import { base64ToBlockDtoBytes } from "../lib/blockDtoWire";
import { blocksToPalette } from "../lib/brandedPreview";
import type { BriefFormat } from "../lib/fileIngest";
import { isValidSlug, slugify } from "../lib/slug";
import type {
  DoczBinarySource,
  DoczBinaryTransport,
  DoczTransport,
  DoczTransportBlock,
} from "../lib/doczHandle";
import { DOCUMENT_MEDIA_TYPE } from "../lib/doczHandle";
import type { ElaborateApi } from "./client";
import { createRealAdapter } from "./realAdapter";
import type {
  ArticleUnitPatchWire,
  ArticleUnitRowWire,
  DeletePlanWire,
  DocumentsDisposition,
  MergePartsWire,
  MergeResultWire,
  PartPlanWire,
  UnitDocumentWire,
} from "./articleAdminTypes";
import { ApiError } from "./types";
import { projectDeletes } from "../lib/commentDeleteProjection";
import type {
  AnchorState,
  Comment,
  CommentBodyEdit,
  CommentCreate,
  CommentEditResponse,
  CommentStatus,
  DocStatus,
  ReactionToggleResponse,
  ReplyCreate,
  StatusChange,
  VersionRestoreReport,
  VisibilityChange,
  VisibilityChangeResponse,
  WorkspaceMember,
} from "./types";

export type { DoczTransport } from "../lib/doczHandle";

/** The closed 3-value resource slot (design §1.2) — identical to the workbench
 *  `Kind`, re-exported so the workbench components + this adapter share one type. */
export type ResourceRole = Kind;

/** Which side of the ARTICLE a comment addresses → which gutter it lands in
 *  (design §1.7 `pairing_side`). */
export type PairingSide = "brief" | "research" | "none";

/** The server's version-aware anchor state (design §1.7 · docz RebaseOutcome +
 *  the disjoint `unanchored`): the commit-time `docz.anchor.rebase` outcome the
 *  comment card surfaces.
 *
 *  This is an ALIAS, not a second copy. The gutter (`WorkbenchComment.
 *  anchor_state`) and the tickets grid (`Ticket.anchor_state`) read the SAME
 *  server column through the SAME projection — `models.anchor_state_sql`, one
 *  comptime string on the Zig side — so they get one type here too. Two hand-
 *  maintained unions over one wire value is exactly how `unanchored` reached the
 *  client as an unhandled string in the first place. */
export type RebaseState = AnchorState;

/**
 * A review comment plus the two layout-routing columns the article pivot adds
 * (design §1.7): `home_resource` (the pane its selector anchors into) and
 * `pairing_side` (which side of the article it addresses). These are the pure
 * inputs the gutter-placement function reads (design §2.5). Legacy single-pane
 * comments carry `home_resource='article', pairing_side='none'`.
 *
 * `anchor_state` + `anchored_seq` are the SERVER version-aware fields (§1.7): the
 * durable commit-time rebase outcome (exact/shifted/repaired/orphaned) and the
 * section version the anchor now resolves against (drives "quote moved in v{n}").
 */
export interface WorkbenchComment extends Comment {
  home_resource: ResourceRole;
  pairing_side: PairingSide;
  anchor_state?: RebaseState;
  anchored_seq?: number;
}

/**
 * One cross-resource section link with its DERIVED per-endpoint staleness
 * (design §1.5/§1.6 · `GET /api/article-units/:id/links` → `models.SectionLink`).
 * A link is stale on an endpoint when its pin lags that section's head; the
 * article shows "refers to {other_role} v{other_pin_seq}". Field names ARE the
 * backend contract keys.
 */
export interface SectionLink {
  id: number;
  link_type: "brief_article" | "research_article";
  origin: string;
  article_section_id: number;
  other_section_id: number;
  other_role: ResourceRole;
  article_heading: string;
  other_heading: string;
  other_pin_seq: number;
  other_head_seq: number;
  article_pin_seq: number;
  article_head_seq: number;
  stale_other: boolean;
  stale_article: boolean;
}

/**
 * One heading-delimited section version (design §1.4) — the pin / staleness /
 * timeline / anchor unit. A row exists only when THAT section changed
 * (independent `seq`).
 */
export interface SectionVersion {
  id: string;
  section_id: string;
  seq: number;
  heading_text: string;
  content_sha256: string;
  change_kind: "added" | "modified" | "moved" | "unchanged";
  editor_name: string;
  created_at: string;
}

/**
 * The whole-resource save-back body (spec D1 · `POST /api/documents/:id/content`).
 * brief/article commit `rendered_html` (the WYSIWYG editor's `toHtml()`);
 * research commits `body_md`. `note` is an optional per-save annotation carried
 * onto the changed sections' `el.section_versions.note`.
 */
export interface ResourceSavePayload {
  rendered_html?: string;
  body_md?: string;
  note?: string;
  /**
   * "Yes, empty this resource on purpose."
   *
   * The server REFUSES (409 `destructive_clear_refused`) a save that would
   * replace a document that has content with an empty one, unless this is set.
   * Six delivered client documents were emptied on 2026-07-27 by a caller that
   * posted a body the server read as empty and got a 201 back; the guard makes
   * that impossible by accident while keeping a deliberate clear possible.
   *
   * Only a UI whose explicit purpose is "clear this document" should send it.
   * Never default it on, and never set it to retry a 409 — a 409 here means the
   * bytes you computed were empty and you probably did not mean them to be.
   *
   * Every key in this interface is proven to be a declared field of the Zig
   * `handlers/doc_content.zig:SaveBody` by `docContent.conformance.test.ts`.
   */
  confirm_clear?: boolean;
}

/** One section the save advanced (spec E · `models.ChangedSection`). */
export interface ChangedSection {
  section_id: string;
  seq: number;
  change_kind: "added" | "modified" | "moved" | "unchanged";
}

/**
 * The whole-resource save result (spec E · `models.ResourceSaveResult`): the new
 * doc head hash + the per-section streams the save advanced. Unchanged sections
 * are absent (independent `seq` — only changed sections version).
 */
export interface ResourceSaveResult {
  head_hash: string;
  changed_sections: ChangedSection[];
}

/** The render-ready branded body (`GET /api/documents/:id/branded-docz`): the
 *  document title + the docz block-DTO list bytes (`encodeBlockList` output) the
 *  branded canvas preview lays out byte-identically to the PDF body. */
export interface BrandedBody {
  readonly title: string;
  readonly bodyDto: Uint8Array;
}

/** A durable section identity (design §1.3) with its per-section version head. */
export interface Section {
  id: string;
  role: ResourceRole;
  ord: number;
  heading: string;
  /** `data-block-id` of the section's FIRST block (`"b{block_lo}"`) — the DOM
   *  anchor the hover-handle maps its margin grip to (design §1.2). */
  anchor_key: string;
  head_version_id: string;
  content_hash: string;
  versions: SectionVersion[];
  /** SERVER-STATED block span `[block_lo, block_hi)` over the resource's flat
   *  `.docz-block` spine (design §1.4 hardening) — the explicit section region so
   *  the DOM→section map is stated by the wire, not inferred from the neighbour's
   *  `anchor_key`. `block_hi` is EXCLUSIVE; a `< 0` / undefined `block_hi` means
   *  "runs to the end of the body" (the last section). Optional so a legacy wire
   *  (or the client-side `anchor_key` fallback) still degrades gracefully. */
  block_lo?: number;
  block_hi?: number;
  /** Derived pin-vs-head staleness (design §1.6); precomputed in the mock. */
  stale?: boolean;
}

/**
 * One of the three role-tagged resources (design §1.2). brief/article carry docz
 * IR → rendered HTML (`format='html'`); research carries markdown source
 * (`format='md'`). Comments key on `doc_id` (the `el.documents` id) exactly as
 * today, so the review layer is unchanged.
 */
export interface ArticleResource {
  role: ResourceRole;
  doc_id: string;
  title: string;
  format: "html" | "md";
  /** Head version seq — the human "v3" the header chip shows. */
  seq: number;
  /** Head `content_sha256` — the header renders its first 8 chars. */
  content_hash: string;
  /** brief / article. */
  rendered_html?: string;
  /** research. */
  body_md?: string;
  sections: Section[];
  /** Any section stale (design §1.6) → the header stale pin. */
  stale?: boolean;
}

/**
 * The article-unit LIBRARY-list row (design §5 · `GET /api/article-units` →
 * `models.ArticleUnit`). The lean summary the Articles landing renders as a card;
 * the full `{ brief, research, article }` aggregate is fetched per-unit by
 * `getArticleUnit`. Field names ARE the backend contract keys.
 */
export interface ArticleUnitSummary {
  id: string;
  workspace_slug: string;
  title: string;
  slug: string;
  brand: string;
  lang: string;
  status: DocStatus;
  section_level: number;
  brief_doc_id: number;
  /** How many of the 3 role resources (brief/research/article) exist for the unit. */
  resource_count: number;
  delivered_at: string | null;
  created_at: string;
}

/**
 * The create-an-article-project request (`POST /api/article-units`).
 *
 * ── THE WIRE CONTRACT — THE SERVER OWNS THESE NAMES ────────────────────
 * Every field below is spelled EXACTLY as `src/handlers/article_units.zig`'s
 * `CreateBody` declares it, and it is carried under that name all the way from
 * `lib/briefIngest.ts` to `JSON.stringify` — there is no renaming step, because
 * a renaming step is where this contract broke in production: the SPA posted
 * `format` / `filename` / `content_b64` / `enqueue_agent`, Zig's decoder dropped
 * all four in silence, and every "successful" 201 created a project with an
 * EMPTY brief that starved the agent queue. `articleCreate.conformance.test.ts`
 * reads the Zig struct as text and fails the build on any drift.
 *
 *   workspace           the target workspace slug (membership-gated; non-member → 404)
 *   title               required, trimmed; empty → 400
 *   slug                optional; empty ⇒ the SERVER derives it from the title. The
 *                       client sends its preview so a user-edited slug is honoured,
 *                       and a duplicate is a 409 (UNIQUE(workspace_id, slug))
 *   brief_format        "md" | "docx" | "html" | ""  — empty ⇒ no brief bytes at all
 *                       (a legal EMPTY brief; the user types it in the editor)
 *   brief_filename      provenance + the server's format fallback. NEVER a path: the
 *                       server uses it only for `source_name` + extension inference
 *   brief_content_b64   base64 of the raw brief bytes ("" when brief_format is "")
 *   enqueue_agent_work  create the `el.briefings` work item in the SAME transaction,
 *                       so an agent can poll → claim → research → propose. Off ⇒ a
 *                       human-authored project no agent will pick up
 */
export interface ArticleUnitCreate {
  title: string;
  slug?: string;
  brand?: string;
  lang?: string;
  brief_format: BriefFormat | "";
  brief_filename?: string;
  brief_content_b64: string;
  enqueue_agent_work: boolean;
}

/**
 * The create response. `ingest_ok` is the honest half: `renderBytes` NEVER fails
 * on a malformed source — it degrades to a fallback card — so a corrupt .docx
 * would otherwise create a project whose brief is a polite placeholder and say
 * nothing. When `ingest_ok` is false the dialog surfaces `note` instead of a
 * silent success.
 */
export interface ArticleUnitCreated {
  unit: ArticleUnitSummary;
  ingest_ok: boolean;
  note: string;
}

// ── article-project LIFECYCLE inputs (rename / delete / merge) ───────────────
//
// The reply shapes are the SERVER's, re-exported verbatim from
// `./articleAdminTypes` — the dialogs render the server's own inventory and its
// own exchange plan, never a client-side guess about what a delete or a merge
// will do.
export type {
  DeletePlanWire,
  DocumentsDisposition,
  MergeResultWire,
  PartPlanWire,
  UnitDocumentWire,
} from "./articleAdminTypes";

/** The rename/re-slug patch. Every field is ABSENT-means-unchanged; only
 *  `clear_target_words` can put a value back to NULL. Mirrors
 *  `handlers/article_units.zig:PatchBody`. */
export type ArticleUnitPatch = ArticleUnitPatchWire;

/** What a delete should do, as the CALLER states it. There is no default that
 *  destroys — the dialog makes the user choose, and `dry_run` previews. */
export interface DeleteArticleOptions {
  /** `detach` leaves every document live in the library; `archive` additionally
   *  soft-archives them (they leave every read surface and stay restorable —
   *  nothing is erased). */
  documents: DocumentsDisposition;
  /** Preview: compute the inventory and write NOTHING. */
  dry_run?: boolean;
  /** The project's own slug, echoed back. REQUIRED by the server for `archive`
   *  and compared there — never only in the modal. */
  confirm?: string;
}

/** Which project each role's document is taken from. An omitted role is left
 *  alone. Mirrors `handlers/article_units.zig:PartsBody`. */
export interface MergePartsInput {
  brief_from_unit_id?: number;
  article_from_unit_id?: number;
  research_from_unit_id?: number;
  /** Preview: compute the exchange and write NOTHING. */
  dry_run: boolean;
}

/** The article-unit aggregate (design §1.1). */
export interface ArticleUnit {
  id: string;
  slug: string;
  title: string;
  brand: string;
  lang: string;
  /** Reuses the existing `<StatusBadge>` vocabulary. */
  status: DocStatus;
  /** Heading level delimiting sections (H2 default, design §1.1). */
  section_level: number;
  /** Bottom-bar target word count N (design §4 · N1). `undefined`/null ⇒ the
   *  article pane's word-count chip shows just `n` (no `/N`). */
  targetWords?: number;
  resources: Record<ResourceRole, ArticleResource>;
}

/**
 * The article-unit API surface (design §2.8). Two implementations satisfy it:
 * the in-memory mock (below) and — at W1 integration — a real adapter over
 * `/api/article-units`. Every method returns the SAME shapes the backend will.
 */
export interface ArticleUnitsApi {
  /**
   * The workspace's article-unit library list (design §5 · `GET
   * /api/article-units?workspace=slug`) — the article-first landing's card source.
   */
  listArticleUnits(workspaceSlug: string): Promise<ArticleUnitSummary[]>;
  /**
   * Create an article project from a brief (`POST /api/article-units`) — the
   * write half of the article-unit surface. One server transaction creates the
   * unit + its three role-tagged resources (`article_units_repo.createUnit`) and,
   * when `enqueue_agent` is set, the `el.briefings` work item that makes it
   * visible to agents. See {@link ArticleUnitCreate} for the wire contract.
   */
  createArticleUnit(
    workspaceSlug: string,
    input: ArticleUnitCreate,
  ): Promise<ArticleUnitCreated>;
  /**
   * Rename / re-slug / re-brand a project (`PATCH /api/article-units/:id`).
   * `edit_doc`-gated server-side; a duplicate slug is a 409 `slug_taken` whose
   * message the dialog renders inline. Returns the updated row in the SAME
   * projection the library list uses, so the row re-renders with one decoder.
   */
  renameArticleUnit(
    unitId: string,
    patch: ArticleUnitPatch,
  ): Promise<ArticleUnitSummary>;
  /**
   * Delete a project (`DELETE /api/article-units/:id`) — `admin_workspace`-gated.
   *
   * With `dry_run` it writes NOTHING and returns the FULL document inventory
   * (title, role, real byte size, version + comment counts, delivery date) the
   * confirmation dialog renders. Neither disposition ever erases a document row:
   * `detach` leaves them live in the library, `archive` soft-archives them
   * (recoverable). Saying "permanently delete" in the UI would be a lie.
   */
  deleteArticleUnit(
    unitId: string,
    opts: DeleteArticleOptions,
  ): Promise<DeletePlanWire>;
  /** Same-workspace projects this one could take parts from
   *  (`GET /api/article-units/:id/parts`) — the merge dialog's source pickers. */
  listMergeCandidates(unitId: string): Promise<ArticleUnitSummary[]>;
  /**
   * Compose this project from other projects' parts
   * (`POST /api/article-units/:id/parts`).
   *
   * A role slot is EXCHANGED, never emptied: the target's current document for
   * that role moves INTO the source project. `dry_run` returns the exact plan —
   * what moves in, what is displaced and where it goes — which the dialog must
   * show before the user commits.
   */
  mergeArticleParts(
    unitId: string,
    input: MergePartsInput,
  ): Promise<MergeResultWire>;
  getArticleUnit(slug: string, unitId: string): Promise<ArticleUnit>;
  /** The unit's comments, each carrying `home_resource` + `pairing_side`. */
  listComments(unitId: string): Promise<WorkbenchComment[]>;
  /**
   * Create an anchored thread ROOT on one resource document of the unit (§2.5
   * dual-gutter authoring). `docId` is the pane's `el.documents` id
   * (`resource.doc_id`); the created comment inherits that document's
   * `resource_role` as its `home_resource`, so it routes to the pane's gutter.
   * Proxies the base `POST /api/documents/:id/comments` the DocumentViewer uses.
   */
  createComment(docId: string, body: CommentCreate): Promise<Comment>;
  /**
   * Reply to a comment (no new anchor; the reply hangs off the addressed root).
   * Proxies the base `POST /api/comments/:cid/replies`. Persists — survives a
   * reload (the W1 workbench only mutated local state, losing the reply).
   */
  replyComment(commentId: number, body: ReplyCreate): Promise<Comment>;
  /**
   * Change a comment's review status. Proxies the base `PATCH
   * /api/comments/:cid/status`. Persists — survives a reload.
   */
  setCommentStatus(
    commentId: number,
    status: CommentStatus,
  ): Promise<StatusChange>;
  /**
   * comment-visibility (§3.6): change a thread ROOT's scope after creation.
   * Proxies the base `PATCH /api/comments/:cid/visibility` (author-or-staff,
   * root-only — enforced server-side).
   */
  setCommentVisibility(
    commentId: number,
    body: VisibilityChange,
  ): Promise<VisibilityChangeResponse>;
  /**
   * review-notes §3: edit a comment/reply body (AUTHOR-ONLY). Proxies the base
   * `PATCH /api/comments/:cid`. Persists — survives a reload.
   */
  /** comment-delete: DELETE a comment or reply (AUTHOR-ONLY, enforced
   *  server-side). The server TOMBSTONES — a note with no replies vanishes from
   *  the next read, one with replies returns as a content-free placeholder — so
   *  the caller MUST reload rather than splice its local list. Resolves to void. */
  deleteComment(commentId: number): Promise<void>;
  editComment(
    commentId: number,
    body: CommentBodyEdit,
  ): Promise<CommentEditResponse>;
  /**
   * review-notes §4: toggle the caller's emoji reaction on a comment/reply.
   * Proxies the base `PUT/DELETE /api/comments/:cid/reactions/:emoji` — visibility-
   * gated + PK-deduped server-side. Persists — survives a reload.
   */
  toggleReaction(
    commentId: number,
    emoji: string,
    add: boolean,
  ): Promise<ReactionToggleResponse>;
  /** The @-mention combobox source. */
  listMembers(unitId: string): Promise<WorkspaceMember[]>;
  /** Timeline-bar ticks for a resource (design §2.4). */
  resourceVersions(unitId: string, role: ResourceRole): Promise<VersionTick[]>;
  /**
   * REVERT a resource to one of its own past revisions — `git revert`, never
   * `git reset`: the server appends a NEW revision whose content equals
   * `versionId` and leaves every revision in between exactly where it is.
   *
   * Deliberately the EXISTING restore terminal rather than a second write path.
   * That terminal already owns the `restore_version` capability gate, the
   * pre-revert backup snapshot, the destructive-clear and live-markup refusals,
   * the comment-anchor rebase (a revert is a commit like any other) and the
   * typed provenance column. A parallel "revert" endpoint would have to
   * re-earn all of it, and the first thing it would get wrong is the one the
   * 2026-07-27 incident got wrong.
   */
  revertResource(
    unitId: string,
    role: ResourceRole,
    versionId: string,
    note?: string,
  ): Promise<VersionRestoreReport>;
  /**
   * Server-computed A→B diff (design §2.7). The mock returns a canned
   * `docz_diff.DocumentDiff`; the client lowers it with
   * `documentDiffToVersionDiff` before feeding `<SideBySideDiff>`.
   */
  compareResource(
    unitId: string,
    role: ResourceRole,
    aVersionId: string,
    bVersionId: string,
  ): Promise<DocumentDiff>;
  /**
   * The server-emitted docz transport for a document (spec D1 · `GET
   * /api/documents/:id/docz`) — the input `buildDoczHandle` wraps into the live
   * `DoczHandle` the WYSIWYG `<ElaborateEditor>` mounts over (edit mode).
   */
  getDoczDocument(docId: string): Promise<DoczTransport>;
  /**
   * The BINARY representation of `GET /api/documents/:id`, requested with
   * `Accept: application/vnd.norkron.document;v=1` — the typed block list as raw bytes plus the
   * provenance headers, with no JSON envelope and no base64. This is what the
   * CANVAS mounts; `getDoczDocument` stays for the legacy DOM editor.
   */
  getDoczBinary(docId: string): Promise<DoczBinaryTransport>;
  /**
   * The BINARY representation of ONE ARCHIVED VERSION
   * (`GET /api/documents/:id/versions/:vid` with the same vendor `Accept`) — that
   * revision's body as raw block-DTO bytes plus the provenance headers.
   *
   * This is what the VERSION VIEW mounts. Owner decision D-E: no DOM renders any
   * docz IR anywhere, version history explicitly included, so a past revision is
   * painted through the same canvas engine as the head — which needs the same
   * wire, and this is it. `getVersion` (JSON `DocVersionDetail`) stays for the
   * metadata: the note, the editor, the timestamp, the hash.
   */
  getVersionDoczBinary(
    docId: string,
    versionId: number,
  ): Promise<DoczBinaryTransport>;
  /**
   * The render-ready BRANDED body of a document as the docz block-DTO wire
   * (`GET /api/documents/:id/branded-docz`) — the EXACT body IR the branded PDF
   * lays out (raw_html exploded to typed tables/lists, Cerebr8 styles applied),
   * encoded with the SAME `docz` block codec the pdfz-layout wasm reads. Feeds the
   * in-page `<BrandedCanvasPreview>` so tables render as real tables, not source.
   */
  getBrandedBodyDto(docId: string): Promise<BrandedBody>;
  /**
   * Whole-resource save-back (spec D1 · `POST /api/documents/:id/content`). The
   * editor edits ONE canvas; the server re-carves it and advances only the
   * changed per-section `el.section_versions` streams (design §1.9). Returns the
   * new head hash + the sections the save advanced.
   */
  saveResource(
    unitId: string,
    role: ResourceRole,
    payload: ResourceSavePayload,
  ): Promise<ResourceSaveResult>;
  /**
   * A section's per-section version stream (spec D2 · `GET
   * /api/sections/:id/versions`) — powers the section-grained version chips
   * (design §2.4).
   */
  sectionVersions(sectionId: string): Promise<SectionVersion[]>;
  /**
   * Section-scoped A→B diff (spec D2 · `GET /api/sections/:id/compare`). Returns
   * the SAME upstream `docz_diff.DocumentDiff` shape as `compareResource`, so the
   * client lowers it with `documentDiffToVersionDiff` before `<SideBySideDiff>`.
   */
  compareSection(
    sectionId: string,
    aVersionId: string,
    bVersionId: string,
  ): Promise<DocumentDiff>;
  /**
   * The unit's cross-resource section links + DERIVED staleness (design §1.5/§1.6
   * · `GET /api/article-units/:id/links`). Drives the "refers to brief v{n}"
   * older-version indicator + the ↔ counterpart scroll chips.
   */
  listLinks(unitId: string): Promise<SectionLink[]>;
  /**
   * EXPLICIT human re-pin of a stale link (design §1.6 — never automatic · `POST
   * /api/section-links/:id/repin`): pin both endpoints to their current heads.
   */
  repinLink(linkId: number): Promise<void>;
}

// ── seed content (real Wettformat manuscript, docz-rendered look) ────────────

const ARTICLE_HTML = `
<h1>Willkommensbonus im Test</h1>
<p>Wir haben den Willkommensbonus über ein echtes, verifiziertes Konto
durchgespielt — von der Einzahlung bis zur ersten Auszahlung. Alle Angaben
stammen aus dem eingeloggten Kassenbereich und dem Live-Support.</p>

<h2>Wie der Bonus funktioniert</h2>
<p>Das Angebot verdoppelt die erste Einzahlung bis zu einem festgelegten
Höchstbetrag. Der Bonusbetrag wird sofort nach der Einzahlung gutgeschrieben und
erscheint getrennt vom Echtgeldsaldo in der Kassenübersicht.</p>

<h2>Auszahlungsgeschwindigkeit</h2>
<p>Die Auszahlung wird in rund sieben Minuten abgeschlossen — dieser Satz stammt
aus einem früheren Entwurf und ist als anfechtbare Aussage markiert.</p>
<table>
<thead><tr><th>Methode</th><th>Dauer</th><th>Limit</th></tr></thead>
<tbody>
<tr><td>Sofortüberweisung</td><td>7 Minuten</td><td>4.000 €</td></tr>
<tr><td>Banküberweisung</td><td>1–3 Tage</td><td>10.000 €</td></tr>
</tbody>
</table>
<ul><li>KYC vor der ersten Auszahlung</li><li>Keine Gebühren im Testzeitraum</li></ul>

<h2>Umsatzbedingungen</h2>
<p>Der Bonus muss vor einer Auszahlung mehrfach umgesetzt werden. Die genaue
Anzahl der Umsätze haben wir im eingeloggten Bereich geprüft.</p>
`;

const BRIEF_HTML = `
<h1>Content Brief — Willkommensbonus</h1>
<p>Dieser Brief legt Blickwinkel, Pflicht-Abschnitte und Quellenregeln fest.
Jede Aussage muss auf das Casino oder dessen Support zurückführbar sein.</p>

<h2>Wie der Bonus funktioniert</h2>
<p>Erkläre die Verdopplung der ersten Einzahlung und die getrennte
Gutschrift — aus dem eingeloggten Kassenbereich, nicht aus dem Footer.</p>

<h2>Auszahlungsgeschwindigkeit</h2>
<p>Zeitangaben stammen aus dem eingeloggten Kassenbereich und den
Bestätigungs-E-Mails. Keine nicht reproduzierbare Behauptung.</p>

<h2>Umsatzbedingungen</h2>
<p>Nenne die exakten Umsatzanforderungen und die anrechenbaren
Spielkategorien.</p>
`;

const RESEARCH_MD = `# Auszahlung — Feldnotizen

Erste Auszahlung nach Abschluss der KYC-Prüfung eingeleitet. Bearbeitung
begann werktags; **keine** Wochenend-Verarbeitung.

## Bonus-Gutschrift

Bonusbetrag im Kassenbereich getrennt vom Echtgeldsaldo sichtbar. Sofort
nach Einzahlung gutgeschrieben.

## Umsatz

Wagering als \`35x\` gelistet. Zu prüfen: gilt \`35x\` auf Einzahlung+Bonus
oder nur den Bonus.
`;

const ARTICLE_HEADINGS = [
  "Wie der Bonus funktioniert",
  "Auszahlungsgeschwindigkeit",
  "Umsatzbedingungen",
];

let idSeq = 1;
function nextId(prefix: string): string {
  const n = idSeq;
  idSeq += 1;
  return `${prefix}-${n}`;
}

/** Build one section with a small independent version stream. `stale` marks the
 *  section whose pin lags its head (design §1.6). `blockLo` is the section's first
 *  top-level `.docz-block` index; each seed section is a regular `h2 + p` pair
 *  (2 blocks), so its SERVER-STATED span is `[blockLo, blockLo + 2)` and its
 *  `anchor_key` is `"b{blockLo}"` — exactly what the hover-handle maps against. */
function makeSection(
  role: ResourceRole,
  ord: number,
  heading: string,
  seqs: number,
  stale: boolean,
  blockLo: number,
): Section {
  const sectionId = nextId(`sec-${role}`);
  const versions: SectionVersion[] = [];
  for (let s = 1; s <= seqs; s += 1) {
    versions.push({
      id: nextId(`sv-${role}`),
      section_id: sectionId,
      seq: s,
      heading_text: heading,
      content_sha256: hashQuote(`${role}:${heading}:${s}`).padStart(8, "0"),
      change_kind: s === 1 ? "added" : "modified",
      editor_name: role === "research" ? "Agent Scout" : "Mara Feld",
      created_at: new Date(
        Date.UTC(2026, 6, 18 + s, 9, 0, 0),
      ).toISOString(),
    });
  }
  const head = versions[versions.length - 1];
  return {
    id: sectionId,
    role,
    ord,
    heading,
    anchor_key: `b${blockLo}`,
    block_lo: blockLo,
    block_hi: blockLo + 2,
    head_version_id: head.id,
    content_hash: head.content_sha256,
    versions,
    stale,
  };
}

/** Assemble a fresh unit from the constant seeds (fresh per call → tests never
 *  share mutable state). */
function buildUnit(slug: string, unitId: string): ArticleUnit {
  idSeq = 1; // deterministic ids per unit build
  // The seed HTML is `h1, p, h2, p, h2, p, h2, p` → the three H2-delimited sections
  // anchor at blocks b2 / b4 / b6 (the H1 + lead paragraph are the uncarved lead in
  // this mock). `2 + 2 * i` is the section's first-block index.
  const articleSections = ARTICLE_HEADINGS.map((h, i) =>
    makeSection("article", i, h, i === 1 ? 3 : 1, false, 2 + 2 * i),
  );
  const briefSections = ARTICLE_HEADINGS.map((h, i) =>
    // The brief's "Auszahlungsgeschwindigkeit" advanced past the article's pin.
    makeSection("brief", i, h, i === 1 ? 2 : 1, i === 1, 2 + 2 * i),
  );
  const researchSections = [
    makeSection("research", 0, "Auszahlung — Feldnotizen", 2, false, 0),
    makeSection("research", 1, "Bonus-Gutschrift", 1, false, 2),
    makeSection("research", 2, "Umsatz", 1, false, 4),
  ];

  const article: ArticleResource = {
    role: "article",
    doc_id: `${unitId}-article`,
    title: "Willkommensbonus im Test",
    format: "html",
    seq: 4,
    content_hash: hashQuote(`${unitId}:article:head`).padStart(8, "0"),
    rendered_html: ARTICLE_HTML,
    sections: articleSections,
    stale: false,
  };
  const brief: ArticleResource = {
    role: "brief",
    doc_id: `${unitId}-brief`,
    title: "Content Brief — Willkommensbonus",
    format: "html",
    seq: 2,
    content_hash: hashQuote(`${unitId}:brief:head`).padStart(8, "0"),
    rendered_html: BRIEF_HTML,
    sections: briefSections,
    stale: true,
  };
  const research: ArticleResource = {
    role: "research",
    doc_id: `${unitId}-research`,
    title: "Recherche — Auszahlung & Bonus",
    format: "md",
    seq: 3,
    content_hash: hashQuote(`${unitId}:research:head`).padStart(8, "0"),
    body_md: RESEARCH_MD,
    sections: researchSections,
    stale: false,
  };

  return {
    id: unitId,
    slug,
    title: "Willkommensbonus im Test",
    brand: "Wettformat",
    lang: "de",
    status: "draft",
    section_level: 2,
    // Bottom-bar target N — the mock article pane shows `n/N` (design §4 · N1).
    targetWords: 1200,
    resources: { brief, research, article },
  };
}

/** A small deterministic article-unit library the Articles landing renders with
 *  NO backend (same seed vocabulary as `buildUnit`; brand/lang/status vary so the
 *  status filter + role chips are exercisable). */
function buildUnitSummaries(slug: string): ArticleUnitSummary[] {
  const rows: Array<{
    id: number;
    title: string;
    brand: string;
    lang: string;
    status: DocStatus;
    resource_count: number;
    delivered_at: string | null;
  }> = [
    {
      id: 1,
      title: "Willkommensbonus im Test",
      brand: "Wettformat",
      lang: "de",
      status: "draft",
      resource_count: 3,
      delivered_at: null,
    },
    {
      id: 2,
      title: "Betmatch Casino Review",
      brand: "Betmatch",
      lang: "en",
      status: "delivered",
      resource_count: 3,
      delivered_at: "2026-07-22T09:00:00Z",
    },
    {
      id: 3,
      title: "Goldenplay Casino Testbericht",
      brand: "Goldenplay",
      lang: "de",
      status: "superseded",
      resource_count: 2,
      delivered_at: null,
    },
  ];
  return rows.map((r) => ({
    id: String(r.id),
    workspace_slug: slug || "wettformat",
    title: r.title,
    slug: `article-${r.id}`,
    brand: r.brand,
    lang: r.lang,
    status: r.status,
    section_level: 2,
    brief_doc_id: 100 + r.id,
    resource_count: r.resource_count,
    delivered_at: r.delivered_at,
    created_at: new Date(Date.UTC(2026, 6, 20 - r.id, 9, 0, 0)).toISOString(),
  }));
}

/** The three role documents of a seed project, with the real numbers a delete
 *  confirmation must state (`db/article_unit_admin_repo.zig:UnitDocument`). The
 *  MOCK counterpart of the server's inventory — same field names, same ordering
 *  (brief → article → research), so the dialog is exercisable with no backend. */
function mockUnitDocumentInventory(u: ArticleUnitSummary): UnitDocumentWire[] {
  const base = Number(u.id) * 10;
  const rows: Array<[ResourceRole, string, number, number, number]> = [
    ["brief", "html", 8_192, 1, 0],
    ["article", "html", 41_984, 4, 3],
    ["research", "md", 3_072, 2, 1],
  ];
  return rows
    .slice(0, Math.max(1, Math.min(3, u.resource_count)))
    .map(([role, format, bytes, versions, comments], i) => ({
      document_id: base + i + 1,
      resource_role: role,
      title: `${u.title} — ${role}`,
      format,
      byte_size: bytes,
      version_count: versions,
      comment_count: comments,
      delivered_at: role === "article" ? u.delivered_at : null,
    }));
}

/** The summary back in its WIRE row shape (`db/models.zig:ArticleUnit`) — what
 *  the merge reply embeds under `unit`. */
function mockRowFromSummary(u: ArticleUnitSummary): ArticleUnitRowWire {
  return {
    id: Number(u.id),
    workspace_slug: u.workspace_slug,
    title: u.title,
    slug: u.slug,
    brand: u.brand,
    lang: u.lang,
    status: u.status,
    section_level: u.section_level,
    brief_doc_id: u.brief_doc_id,
    resource_count: u.resource_count,
    delivered_at: u.delivered_at,
    created_at: u.created_at,
  };
}

/** Seed comments across the three panes, each tagged with the routing columns so
 *  the gutter-placement function (design §2.5) can be exercised end-to-end. */
function buildComments(): WorkbenchComment[] {
  let cid = 1;

  const base = (over: Partial<WorkbenchComment>): WorkbenchComment => ({
    id: cid++,
    parent_id: null,
    author_id: 100,
    type: "comment",
    body_format: "markdown",
    block_ref: "b0",
    quote: "",
    prefix: "",
    suffix: "",
    start: 0,
    end: 0,
    quote_hash: "",
    body: "",
    status: "open",
    author_name: "Mara Feld",
    created_at: "2026-07-20T09:00:00Z",
    mentions: [],
    home_resource: "article",
    pairing_side: "none",
    ...over,
  });

  return [
    // article-homed, addresses the BRIEF → brief-article gutter.
    base({
      home_resource: "article",
      pairing_side: "brief",
      type: "request-for-change",
      block_ref: "b3",
      quote: "in rund sieben Minuten abgeschlossen",
      quote_hash: hashQuote("in rund sieben Minuten abgeschlossen"),
      body: "This payout-time claim is **not reproducible** — the brief says to drop anything we could not time first-hand.",
      author_name: "Mara Feld",
      created_at: "2026-07-20T09:12:00Z",
    }),
    // article-homed, addresses RESEARCH → article-research gutter.
    base({
      home_resource: "article",
      pairing_side: "research",
      type: "suggestion",
      block_ref: "b5",
      quote: "mehrfach umgesetzt werden",
      quote_hash: hashQuote("mehrfach umgesetzt werden"),
      body: "Add the exact `35x` figure from the research notes here.",
      author_name: "Dana Okonkwo",
      status: "open",
      created_at: "2026-07-20T10:03:00Z",
    }),
    // brief-homed → brief-article gutter.
    base({
      home_resource: "brief",
      pairing_side: "none",
      type: "comment",
      block_ref: "b2",
      quote: "getrennte\nGutschrift",
      quote_hash: hashQuote("getrennte Gutschrift"),
      body: "The article covers this cleanly — no change needed on the brief side.",
      author_name: "Kai Brandt",
      status: "resolved",
      created_at: "2026-07-19T15:40:00Z",
    }),
    // research-homed → article-research gutter.
    base({
      home_resource: "research",
      pairing_side: "none",
      type: "comment",
      block_ref: "b1",
      quote: "keine",
      quote_hash: hashQuote("keine"),
      body: "Confirm the weekend-processing note with a second withdrawal.",
      author_name: "Agent Scout",
      created_at: "2026-07-21T11:00:00Z",
    }),
    // reply on the first thread (no anchor).
    {
      id: cid++,
      parent_id: 1,
      author_id: 101,
      type: "comment",
      body_format: "markdown",
      block_ref: "",
      quote: "",
      prefix: "",
      suffix: "",
      start: 0,
      end: 0,
      quote_hash: "",
      body: "Confirmed on the cashier: `0–2h`, never 7 minutes. Agreed it must go.",
      status: "open",
      author_name: "Kai Brandt",
      created_at: "2026-07-20T10:20:00Z",
      mentions: [],
      home_resource: "article",
      pairing_side: "brief",
    },
  ];
}

const MEMBERS: WorkspaceMember[] = [
  { id: 100, name: "Mara Feld", email: "mara@wettformat.de" },
  { id: 101, name: "Kai Brandt", email: "kai@wettformat.de" },
  { id: 102, name: "Dana Okonkwo", email: "dana@wettformat.de" },
];

/** A canned block-level diff for the article's "Auszahlungsgeschwindigkeit"
 *  section: the "7 minutes" paragraph is `change`d and a caveat is `insert`ed —
 *  the exact `docz_diff.DocumentDiff` shape the WASM core emits. */
function articlePayoutDiff(): DocumentDiff {
  return {
    ops: [
      {
        op: "equal",
        oldIndex: 0,
        newIndex: 0,
        kind: "heading",
        oldBlockHtml: "<h2>Auszahlungsgeschwindigkeit</h2>",
        newBlockHtml: "<h2>Auszahlungsgeschwindigkeit</h2>",
      },
      {
        op: "change",
        oldIndex: 1,
        newIndex: 1,
        kind: "paragraph",
        // A stable content-addressed change id — the mock MIRRORS the real wire
        // (backend `diff_block.toModel` now stamps `changeHash` on every non-equal
        // op) so the client's deep-link + note-anchor paths exercise identically.
        changeHash: "a1b2c3d4e5f60718",
        oldBlockHtml:
          "<p>Die Auszahlung wird in rund sieben Minuten abgeschlossen.</p>",
        newBlockHtml:
          "<p>Die Auszahlung wurde nach der KYC-Prüfung eingeleitet; die Dauer hängt von der Methode ab.</p>",
        text: {
          oldText: "Die Auszahlung wird in rund sieben Minuten abgeschlossen.",
          newText:
            "Die Auszahlung wurde nach der KYC-Prüfung eingeleitet; die Dauer hängt von der Methode ab.",
          edits: [
            { op: "equal", text: "Die Auszahlung w" },
            { op: "delete", text: "ird in rund sieben Minuten abgeschlossen" },
            {
              op: "insert",
              text: "urde nach der KYC-Prüfung eingeleitet; die Dauer hängt von der Methode ab",
            },
            { op: "equal", text: "." },
          ],
        },
      },
      {
        op: "insert",
        newIndex: 2,
        kind: "paragraph",
        changeHash: "0918273645546372",
        newBlockHtml:
          "<p>Für methodenspezifische Zeiten verweisen wir auf die Banking-Übersicht.</p>",
      },
    ],
    counts: { equal: 1, inserted: 1, deleted: 0, moved: 0, changed: 1 },
  };
}

/**
 * Reverts performed in THIS mock session, per `doc_id`, in the order they
 * happened. The mock synthesises its ticks from a static `seq` counter, so a
 * revert has nowhere to land unless it is recorded here — and a demo mode in
 * which "revert" visibly does nothing teaches the exact falsehood the feature
 * exists to disprove.
 */
const mockReverts = new Map<string, Array<{ sourceSeq: number }>>();

/** Reset hook for tests (mirrors `resetMockSharedState`). */
export function resetMockReverts(): void {
  mockReverts.clear();
}

function versionTicks(res: ArticleResource): VersionTick[] {
  // One tick per HEAD version of the resource (index-spaced, ascending seq).
  const ticks: VersionTick[] = [];
  for (let s = 1; s <= res.seq; s += 1) {
    ticks.push({
      versionId: `${res.doc_id}-v${s}`,
      seq: s,
      label: `v${s}`,
      isHead: s === res.seq,
      createdAt: new Date(Date.UTC(2026, 6, 17 + s, 9, 0, 0)).toISOString(),
      stale: res.stale && s === res.seq ? true : undefined,
      commentCount: s === res.seq ? (res.role === "article" ? 2 : 1) : undefined,
      author: res.role === "research" ? "Agent Scout" : "Mara Feld",
    });
  }
  // Every revert APPENDS — it never rewrites or removes a tick above. That is
  // the whole semantic, so the mock models it the same way the server does.
  for (const [i, r] of (mockReverts.get(res.doc_id) ?? []).entries()) {
    const seq = res.seq + i + 1;
    const prev = ticks[ticks.length - 1];
    if (prev) prev.isHead = false;
    ticks.push({
      versionId: `${res.doc_id}-v${seq}`,
      seq,
      label: `v${seq}`,
      isHead: true,
      createdAt: new Date(Date.UTC(2026, 6, 17 + seq, 9, 0, 0)).toISOString(),
      author: "Dominik",
      revertOf: {
        versionId: `${res.doc_id}-v${r.sourceSeq}`,
        label: `v${r.sourceSeq}`,
      },
    });
  }
  return ticks;
}

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), 40));
}

/**
 * The MOCK binary transport for one HTML body — the head's and an archived
 * version's, from one place.
 *
 * The mock has no docz codec, so it encodes the same blocks the JSON mock
 * produces through `@norkron/web-docz`'s OWN `encodeBlockList`: the identical wire
 * the real server emits, so a surface built against the mock decodes exactly what
 * it will decode in production. `source` is `legacy-html` because that is the
 * truth about a mock — there is no stored structured blob anywhere in it.
 */
function mockBinaryOf(docId: string, html: string): DoczBinaryTransport {
  const t = htmlToTransport(docId, html);
  return {
    docId,
    bytes: encodeBlockList(
      t.blocks.map((b) =>
        b.kind === "heading"
          ? {
              kind: "heading" as const,
              level: b.level ?? 2,
              runs: [{ text: b.text, marks: [] }],
            }
          : { kind: "paragraph" as const, runs: [{ text: b.text, marks: [] }] },
      ),
    ),
    source: "legacy-html" as DoczBinarySource,
    hash: "",
    contentType: DOCUMENT_MEDIA_TYPE,
    baseVersionId: t.base_version_id,
  };
}

/** Derive a block transport from one of the seed HTML manuscripts (mock only —
 *  the REAL transport is server-emitted from the canonical docz IR). Plain-text
 *  block extraction over the top-level `<h1..6>` / `<p>` runs; `b<i>` block ids
 *  mirror the backend's `blocks_read.buildView` shape. */
function htmlToTransport(docId: string, html: string): DoczTransport {
  const blocks: DoczTransportBlock[] = [];
  // Top-level block elements in document order. `h1–6`/`p` carry inline text
  // (heading/paragraph blocks); `table`/`ul`/`ol`/`blockquote`/`pre` are OPAQUE
  // rich blocks the plain-text transport cannot model — they ride as `kind:'html'`
  // whose `text` is VERBATIM markup, exactly as the live backend's `raw_html`
  // wire. The canvas encode path explodes them back into real elements
  // (web-docz `explodeRawHtml`) so they render instead of painting as source text.
  const re =
    /<(h[1-6]|p|table|ul|ol|blockquote|pre)(?:\s[^>]*)?>([\s\S]*?)<\/\1>/gi;
  let m: RegExpExecArray | null = re.exec(html);
  let i = 0;
  while (m !== null) {
    const tag = m[1].toLowerCase();
    const isHeading = tag[0] === "h" && tag !== "hr";
    const isText = isHeading || tag === "p";
    if (isText) {
      const text = m[2].replace(/\s+/g, " ").trim();
      blocks.push({
        id: `b${i}`,
        text,
        kind: isHeading ? "heading" : "paragraph",
        ...(isHeading ? { level: Number(tag[1]) } : {}),
      });
    } else {
      // Verbatim markup (the FULL element) — the opaque raw_html transport block.
      blocks.push({ id: `b${i}`, text: m[0], kind: "html" });
    }
    i += 1;
    m = re.exec(html);
  }
  return { doc_id: docId, base_version_id: `${docId}-head`, blocks };
}

/** A small canned per-section version stream (mock only) — two heads so the
 *  section chip's Compare has an A→B pair. */
function mockSectionVersions(sectionId: string): SectionVersion[] {
  return [1, 2].map((s) => ({
    id: `${sectionId}-v${s}`,
    section_id: sectionId,
    seq: s,
    heading_text: "Section",
    content_sha256: hashQuote(`${sectionId}:${s}`).padStart(8, "0"),
    change_kind: s === 1 ? "added" : "modified",
    editor_name: "Mara Feld",
    created_at: new Date(Date.UTC(2026, 6, 17 + s, 9, 0, 0)).toISOString(),
  }));
}

/** Derive the resource role a mock doc_id belongs to (the mock ids are
 *  `${unitId}-${role}`), so a workbench-authored comment inherits the pane's
 *  `home_resource` exactly as the real backend stamps it from the document row. */
function mockRoleFromDocId(docId: string): ResourceRole {
  if (docId.endsWith("-brief")) return "brief";
  if (docId.endsWith("-research")) return "research";
  return "article";
}

/**
 * The in-memory article-unit adapter. Read methods build fresh objects from the
 * constant seeds; the COMMENT set is a per-instance mutable store (lazily seeded
 * from {@link buildComments}) so authoring (create/reply/status) is COHERENT
 * within one adapter instance — a created comment/reply/status survives a
 * re-`listComments` exactly as it survives a reload against the real backend.
 * Each `createArticleUnitsMock()` call gets its OWN store, so nothing leaks
 * between test files.
 */
export function createArticleUnitsMock(): ArticleUnitsApi {
  let commentStore: WorkbenchComment[] | null = null;
  let commentSeq = 900_000;
  const store = (): WorkbenchComment[] => (commentStore ??= buildComments());
  // Saved-document store (docId → last saved rendered_html) backed by
  // sessionStorage — the SAME durability the mock session uses — so a whole-resource
  // save is COHERENT across a real page reload: re-entering edit rebuilds the handle
  // from the SAVED html, exactly as it would against the real backend. The transport
  // is block-level (heading/paragraph kind), so a block-structure edit (e.g.
  // paragraph→heading) survives save→reload here; inline marks are a plain-text-
  // transport limitation of the mock (the real docz IR round-trip is owned by the
  // Zig e2e_document_roundtrip suite).
  const SAVED_KEY = (docId: string) => `ela.mock.doc.${docId}`;
  const savedHtml = {
    get(docId: string): string | undefined {
      try {
        return sessionStorage.getItem(SAVED_KEY(docId)) ?? undefined;
      } catch {
        return undefined;
      }
    },
    set(docId: string, html: string): void {
      try {
        sessionStorage.setItem(SAVED_KEY(docId), html);
      } catch {
        /* storage unavailable → no persistence (read path falls back to seed) */
      }
    },
  };
  // Units created through this adapter instance, newest first. The mock does NOT
  // run the ingest pipeline (renderBytes → docz IR → carve is Zig; a client-side
  // renderer would be a SECOND renderer — RULE ZERO), so a created unit lists and
  // navigates but its brief body is only readable back against the real backend.
  // That readback is `test:e2e-article-create`'s job, not a fixture's.
  const createdUnits: ArticleUnitSummary[] = [];
  // Projects deleted through THIS adapter instance. The seed list is a constant
  // rebuilt per call, so "it is gone" has to be recorded rather than spliced out
  // of a fixture array (which would leak across test files — see the workspace
  // fixture-mutation incident in mockAdapter.ts).
  const deletedUnitIds = new Set<string>();
  let createdSeq = 900;
  return {
    async listArticleUnits(workspaceSlug) {
      // `createdUnits` also holds RENAMED seed rows, so the seed list is filtered
      // by id — otherwise a renamed project would appear twice, once under each
      // title, and the rename would look like a duplication.
      const overridden = new Set(createdUnits.map((u) => u.id));
      return delay([
        ...createdUnits.filter(
          (u) =>
            !deletedUnitIds.has(u.id) &&
            (!workspaceSlug || u.workspace_slug === workspaceSlug),
        ),
        ...buildUnitSummaries(workspaceSlug).filter(
          (u) => !overridden.has(u.id) && !deletedUnitIds.has(u.id),
        ),
      ]);
    },

    async createArticleUnit(workspaceSlug, input) {
      const title = input.title.trim();
      if (title.length === 0) {
        throw new ApiError(400, "empty_title", "Give the article a title.");
      }
      const slug = (input.slug ?? "").trim() || slugify(title);
      if (slug.length === 0) {
        throw new ApiError(400, "bad_slug", "That title has no url-safe form.");
      }
      const taken =
        createdUnits.some((u) => u.slug === slug) ||
        buildUnitSummaries(workspaceSlug).some((u) => u.slug === slug);
      if (taken) {
        throw new ApiError(409, "slug_taken", "That slug is already in use.");
      }
      createdSeq += 1;
      const unit: ArticleUnitSummary = {
        id: String(createdSeq),
        workspace_slug: workspaceSlug || "wettformat",
        title,
        slug,
        brand: input.brand ?? "",
        lang: input.lang || "en",
        status: "draft",
        section_level: 2,
        brief_doc_id: createdSeq * 10,
        resource_count: 3,
        delivered_at: null,
        created_at: new Date().toISOString(),
      };
      createdUnits.unshift(unit);
      return delay({ unit, ingest_ok: true, note: "" });
    },
    // ── lifecycle: rename / delete / merge ───────────────────────────────
    // Coherent WITHIN one adapter instance, exactly like the comment store: a
    // rename survives a re-`listArticleUnits`, and the delete/merge previews
    // report the same numbers the commit reports.
    async renameArticleUnit(unitId, patch) {
      const all = [...createdUnits, ...buildUnitSummaries("")];
      const target = all.find((u) => u.id === unitId);
      if (!target) {
        throw new ApiError(
          404,
          "article_not_found",
          "That article project does not exist, or you are not a member of its workspace.",
        );
      }
      if (patch.title != null && patch.title.trim().length === 0) {
        throw new ApiError(400, "title_required", "Give the article a title.");
      }
      if (patch.slug != null) {
        if (!isValidSlug(patch.slug)) {
          throw new ApiError(
            400,
            "slug_invalid",
            "The slug must be 1-64 lowercase letters, numbers or hyphens, and cannot start or end with a hyphen.",
          );
        }
        if (all.some((u) => u.id !== unitId && u.slug === patch.slug)) {
          throw new ApiError(
            409,
            "slug_taken",
            "An article with that slug already exists in this workspace. Choose a different slug.",
          );
        }
      }
      const next: ArticleUnitSummary = {
        ...target,
        ...(patch.title != null ? { title: patch.title.trim() } : {}),
        ...(patch.slug != null ? { slug: patch.slug } : {}),
        ...(patch.brand != null ? { brand: patch.brand } : {}),
        ...(patch.lang != null ? { lang: patch.lang } : {}),
      };
      const at = createdUnits.findIndex((u) => u.id === unitId);
      if (at >= 0) createdUnits[at] = next;
      else createdUnits.unshift(next);
      return delay(next);
    },

    async deleteArticleUnit(unitId, opts) {
      const all = [...createdUnits, ...buildUnitSummaries("")];
      const target = all.find((u) => u.id === unitId);
      if (!target) {
        throw new ApiError(
          404,
          "article_not_found",
          "That article project does not exist, or you are not a member of its workspace.",
        );
      }
      const documents = mockUnitDocumentInventory(target);
      const dry = opts.dry_run === true;
      // MOCK PARITY WITH THE SERVER GUARD: an `archive` whose confirmation does
      // not name the project is refused server-side. A mock that accepted it
      // would teach the dialog a contract production rejects.
      if (!dry && opts.documents === "archive" && opts.confirm !== target.slug) {
        throw new ApiError(
          409,
          "confirm_mismatch",
          "The confirmation did not match this project's slug, so nothing was deleted.",
        );
      }
      if (!dry) {
        const at = createdUnits.findIndex((u) => u.id === unitId);
        if (at >= 0) createdUnits.splice(at, 1);
        deletedUnitIds.add(unitId);
      }
      return delay({
        unit_id: Number(target.id),
        slug: target.slug,
        documents_disposition: opts.documents,
        dry_run: dry,
        documents,
        documents_detached:
          !dry && opts.documents === "detach" ? documents.length : 0,
        documents_archived:
          !dry && opts.documents === "archive" ? documents.length : 0,
        sections_kept: dry ? 0 : documents.length * 3,
        section_links_removed: 0,
      });
    },

    async listMergeCandidates(unitId) {
      const all = [...createdUnits, ...buildUnitSummaries("")];
      const target = all.find((u) => u.id === unitId);
      return delay(
        all.filter(
          (u) =>
            u.id !== unitId &&
            !deletedUnitIds.has(u.id) &&
            (!target || u.workspace_slug === target.workspace_slug),
        ),
      );
    },

    async mergeArticleParts(unitId, input) {
      const all = [...createdUnits, ...buildUnitSummaries("")];
      const target = all.find((u) => u.id === unitId);
      if (!target) {
        throw new ApiError(
          404,
          "article_not_found",
          "That article project does not exist, or you are not a member of its workspace.",
        );
      }
      const requested: Array<[ResourceRole, number | undefined]> = [
        ["brief", input.brief_from_unit_id],
        ["article", input.article_from_unit_id],
        ["research", input.research_from_unit_id],
      ];
      const named = requested.filter(([, from]) => from != null);
      if (named.length === 0) {
        throw new ApiError(
          400,
          "nothing_to_change",
          "Nothing was changed - the request named no new values.",
        );
      }
      const parts: PartPlanWire[] = [];
      for (const [role, from] of named) {
        if (String(from) === unitId) {
          throw new ApiError(
            400,
            "same_project",
            "A part cannot be taken from the project you are merging into.",
          );
        }
        const src = all.find((u) => u.id === String(from));
        if (!src) {
          throw new ApiError(
            404,
            "article_not_found",
            "That article project does not exist, or you are not a member of its workspace.",
          );
        }
        const incoming = mockUnitDocumentInventory(src).find(
          (d) => d.resource_role === role,
        );
        if (!incoming) {
          throw new ApiError(
            409,
            "source_slot_empty",
            "That project has no document in the role you asked for, so there is nothing to take.",
          );
        }
        const displaced = mockUnitDocumentInventory(target).find(
          (d) => d.resource_role === role,
        );
        parts.push({
          role,
          from_unit_id: Number(src.id),
          from_unit_title: src.title,
          incoming_document_id: incoming.document_id,
          incoming_title: incoming.title,
          incoming_byte_size: incoming.byte_size,
          // A SWAP: the displaced document is never deleted — it lands in the
          // SOURCE project, so both projects keep all three resources.
          displaced_document_id: displaced?.document_id ?? null,
          displaced_title: displaced?.title ?? null,
          displaced_byte_size: displaced?.byte_size ?? 0,
          displaced_to_unit_id: displaced ? Number(src.id) : null,
        });
      }
      return delay({
        dry_run: input.dry_run,
        target_unit_id: Number(target.id),
        parts,
        section_links_removed: 0,
        unit: input.dry_run ? null : mockRowFromSummary(target),
      });
    },

    async getArticleUnit(slug, unitId) {
      if (!slug || !unitId) {
        throw new ApiError(404, "not_found", "No such article unit.");
      }
      // ONE DERIVATION (P3-FIX-4B). `getDoczDocument` — what the EDITOR mounts —
      // has always preferred the saved body over the seed. This did not, so the
      // READ view painted the static seed forever: edit → Save → reload showed
      // the edit in the editor and NOT in the read view. Two derivations of one
      // document at one version disagreeing is a correctness bug (a reviewer can
      // approve text the author never wrote), so the read view now resolves the
      // body through the SAME store, by the SAME key (`doc_id` IS
      // `${unitId}-${role}`). See `articleUnits.readSaveCoherence.test.ts`.
      const unit = buildUnit(slug, unitId);
      for (const role of ["brief", "article"] as const) {
        const saved = savedHtml.get(`${unitId}-${role}`);
        if (saved == null) continue;
        unit.resources[role] = { ...unit.resources[role], rendered_html: saved };
      }
      return delay(unit);
    },
    async listComments(unitId) {
      void unitId;
      // comment-delete: the SAME projection the server and the sibling mock
      // apply — a tombstone with no living reply disappears, one with replies
      // returns content-free. One implementation, imported, never re-derived.
      return delay(projectDeletes(store()).map((c) => ({ ...c })));
    },
    async createComment(docId, body) {
      const role = mockRoleFromDocId(docId);
      const created: WorkbenchComment = {
        id: (commentSeq += 1),
        parent_id: null,
        author_id: 100,
        type: body.type,
        body_format: body.body_format ?? "markdown",
        block_ref: body.block_ref,
        quote: body.quote,
        prefix: body.prefix,
        suffix: body.suffix,
        start: body.start,
        end: body.end,
        quote_hash: body.quote_hash,
        body: body.body,
        status: "open",
        author_name: "You",
        created_at: new Date().toISOString(),
        mentions: MEMBERS.filter((m) => body.mentions.includes(m.id)).map(
          (m) => ({ user_id: m.id, name: m.name }),
        ),
        home_resource: role,
        pairing_side: "none",
        anchor_state: "exact",
        anchored_seq: 1,
        visibility: body.visibility ?? "public",
        shared_with:
          (body.visibility ?? "public") === "shared"
            ? MEMBERS.filter((m) =>
                [...(body.shared_with ?? []), ...body.mentions].includes(m.id),
              ).map((m) => ({ user_id: m.id, name: m.name }))
            : [],
      };
      store().push(created);
      return delay({ ...created } as Comment);
    },
    async replyComment(commentId, body) {
      const parent = store().find((c) => c.id === commentId);
      const created: WorkbenchComment = {
        id: (commentSeq += 1),
        parent_id: commentId,
        author_id: 100,
        type: "comment",
        body_format: body.body_format ?? "markdown",
        block_ref: "",
        quote: "",
        prefix: "",
        suffix: "",
        start: 0,
        end: 0,
        quote_hash: "",
        body: body.body,
        status: "open",
        author_name: "You",
        created_at: new Date().toISOString(),
        mentions: MEMBERS.filter((m) => body.mentions.includes(m.id)).map(
          (m) => ({ user_id: m.id, name: m.name }),
        ),
        home_resource: parent?.home_resource ?? "article",
        pairing_side: parent?.pairing_side ?? "none",
      };
      store().push(created);
      return delay({ ...created } as Comment);
    },
    async setCommentStatus(commentId, status) {
      const c = store().find((x) => x.id === commentId);
      if (c) c.status = status;
      return delay({ id: commentId, status });
    },
    async editComment(commentId, body): Promise<CommentEditResponse> {
      const c = store().find((x) => x.id === commentId);
      if (!c) throw new ApiError(404, "comment_not_found", "No comment.");
      const prev = new Date(c.updated_at ?? c.created_at).getTime();
      const now = new Date(Math.max(Date.now(), prev + 1)).toISOString();
      c.body = body.body;
      c.body_format = body.body_format;
      c.updated_at = now;
      return delay({
        id: commentId,
        body: body.body,
        body_format: body.body_format,
        updated_at: now,
      });
    },
    // comment-delete: TOMBSTONE in the workbench fixture store, mirroring the
    // server. The row survives (so replies keep their thread) with its content
    // destroyed; `listComments` above then drops it unless something living hangs
    // below it — the same projection the real read applies.
    async deleteComment(commentId): Promise<void> {
      const list = store();
      const c = list.find((x) => x.id === commentId);
      if (!c) throw new ApiError(404, "comment_not_found", "No comment.");
      if (c.deleted) throw new ApiError(409, "already_deleted", "Already deleted.");
      c.deleted = true;
      c.body = "";
      c.quote = "";
      c.mentions = [];
      c.shared_with = [];
      c.reactions = [];
      return delay(undefined);
    },
    async setCommentVisibility(
      commentId,
      body,
    ): Promise<VisibilityChangeResponse> {
      const c = store().find((x) => x.id === commentId);
      const shared_with =
        body.visibility === "shared"
          ? MEMBERS.filter((m) => (body.shared_with ?? []).includes(m.id)).map(
              (m) => ({ user_id: m.id, name: m.name }),
            )
          : [];
      if (c) {
        c.visibility = body.visibility;
        c.shared_with = shared_with;
      }
      return delay({ id: commentId, visibility: body.visibility, shared_with });
    },
    async toggleReaction(
      commentId,
      emoji,
      add,
    ): Promise<ReactionToggleResponse> {
      const c = store().find((x) => x.id === commentId);
      if (!c) throw new ApiError(404, "comment_not_found", "No comment.");
      // The workbench mock uses a single demo reactor (id 0) — enough to exercise
      // the chip/count/toggle wiring in the diff-notes gutter.
      const vid = 0;
      const groups = c.reactions ?? [];
      let group = groups.find((g) => g.emoji === emoji);
      if (add) {
        if (!group) {
          group = { emoji, count: 0, reacted_by_me: false, who: [] };
          groups.push(group);
        }
        if (!group.who.some((u) => u.user_id === vid))
          group.who.push({ user_id: vid, name: "You" });
      } else if (group) {
        group.who = group.who.filter((u) => u.user_id !== vid);
      }
      c.reactions = groups.filter((g) => g.who.length > 0);
      const echo = group
        ? {
            emoji,
            count: group.who.length,
            reacted_by_me: group.who.some((u) => u.user_id === vid),
            who: group.who,
          }
        : { emoji, count: 0, reacted_by_me: false, who: [] };
      return delay({ comment_id: commentId, reaction: echo });
    },
    async listMembers() {
      return delay(MEMBERS.map((m) => ({ ...m })));
    },
    async resourceVersions(unitId, role) {
      const unit = buildUnit("wettformat", unitId);
      return delay(versionTicks(unit.resources[role]));
    },
    async revertResource(unitId, role, versionId, _note) {
      const unit = buildUnit("wettformat", unitId);
      const res = unit.resources[role];
      const ticks = versionTicks(res);
      const source = ticks.find((t) => t.versionId === versionId);
      if (!source)
        throw new ApiError(404, "version_not_found", "No such version.");
      // Reverting to the version already shown is a NO-OP, and the mock says so
      // in the report rather than minting a phantom revision — `bytes_written`
      // is exactly the field the UI reads to avoid claiming "reverted" when
      // nothing moved.
      if (source.isHead) {
        return delay({
          document_id: Number(res.doc_id.replace(/\D/g, "")) || 0,
          restored_from_version_id: source.seq,
          backup_version_id: 0,
          doc_version_id: source.seq,
          head_version_id_before: source.seq,
          head_version_id_after: source.seq,
          head_hash_before: res.content_hash,
          head_hash_after: res.content_hash,
          content_hash_before: res.content_hash,
          content_hash_after: res.content_hash,
          content_hash_changed: false,
          bytes_before: 0,
          bytes_after: 0,
          bytes_written: false,
          authoring_source: "rendered_html",
          warning: "",
          verdicts_invalidated: [],
          verdicts_carried: [],
          proposals_staled: 0,
          changed_sections: [],
        } satisfies VersionRestoreReport);
      }
      const log = mockReverts.get(res.doc_id) ?? [];
      log.push({ sourceSeq: source.seq });
      mockReverts.set(res.doc_id, log);
      const newSeq = res.seq + log.length;
      return delay({
        document_id: Number(res.doc_id.replace(/\D/g, "")) || 0,
        restored_from_version_id: source.seq,
        backup_version_id: newSeq - 1,
        doc_version_id: newSeq,
        head_version_id_before: newSeq - 1,
        head_version_id_after: newSeq,
        head_hash_before: res.content_hash,
        head_hash_after: `${res.content_hash}-r${log.length}`,
        content_hash_before: res.content_hash,
        content_hash_after: `${res.content_hash}-r${log.length}`,
        content_hash_changed: true,
        bytes_before: 100,
        bytes_after: 120,
        bytes_written: true,
        authoring_source: "rendered_html",
        warning: "",
        // A revert moves the gate hash, so verdicts pinned to the old head stop
        // counting. The demo must show that, not hide it.
        verdicts_invalidated: ["quality"],
        verdicts_carried: [],
        proposals_staled: 0,
        changed_sections: [],
      } satisfies VersionRestoreReport);
    },
    async compareResource(_unitId, role) {
      // W1: the article payout diff is the canned sample; other resources reuse
      // the same shape (the real endpoint computes per-resource server-side).
      void role;
      return delay(articlePayoutDiff());
    },
    async getDoczDocument(docId) {
      // A prior save in THIS session wins over the static seed, so re-entering
      // edit reflects what was saved (a genuine save→reload round-trip).
      const seed = docId.endsWith("-brief") ? BRIEF_HTML : ARTICLE_HTML;
      const html = savedHtml.get(docId) ?? seed;
      return delay(htmlToTransport(docId, html));
    },
    async getDoczBinary(docId) {
      // MOCK PARITY: the mock has no docz codec, so it encodes the SAME blocks
      // the JSON mock produces through web-docz's own `encodeBlockList` — the
      // identical wire the real server emits. It reports `legacy-html` because
      // that is the truth about a mock: there is no stored structured blob.
      const seed = docId.endsWith("-brief") ? BRIEF_HTML : ARTICLE_HTML;
      const html = savedHtml.get(docId) ?? seed;
      return delay(mockBinaryOf(docId, html));
    },
    async getVersionDoczBinary(docId, versionId) {
      // MOCK PARITY, and the one thing that matters here: a VERSION is NOT the
      // head. The mock therefore serves the SEED body — never `savedHtml` — so a
      // save in this session moves the head and leaves the archived revision
      // where it was. A mock that returned the live body would make a version
      // viewer built against it look correct while painting the head, which is
      // exactly the substitution `VCD-03` exists to catch server-side.
      const seed = docId.endsWith("-brief") ? BRIEF_HTML : ARTICLE_HTML;
      return delay({ ...mockBinaryOf(docId, seed), versionId });
    },
    async getBrandedBodyDto(docId) {
      // Offline fallback: no Zig ingest/explode here, so project the transport
      // blocks to the palette (tables stay as text) and frame the block-DTO wire.
      // The live backend returns the real render-ready body (tables exploded).
      const seed = docId.endsWith("-brief") ? BRIEF_HTML : ARTICLE_HTML;
      const html = savedHtml.get(docId) ?? seed;
      const transport = htmlToTransport(docId, html);
      return delay({
        title: "",
        bodyDto: encodeBlockList(blocksToPalette(transport.blocks)),
      });
    },
    async saveResource(unitId, role, payload) {
      // Persist the saved canvas (docId = `${unitId}-${role}`) so a subsequent
      // getDoczDocument reflects it, then echo a plausible save result: one
      // section advanced to seq 2. The real endpoint re-carves + advances only
      // changed sections.
      // MOCK PARITY WITH THE SERVER GUARD. The real terminal answers 409
      // `destructive_clear_refused` when a save would replace a document that
      // has content with an empty one and the body does not carry
      // `confirm_clear`. A mock that accepted it would teach every UI built
      // against it a contract production rejects — and the whole point of the
      // guard is that emptying a delivered document must never be something a
      // caller does without meaning to.
      const incoming = payload.rendered_html;
      if (typeof incoming === "string" && incoming.trim() === "" && !payload.confirm_clear) {
        const stored = savedHtml.get(`${unitId}-${role}`);
        const hasContent = typeof stored === "string" ? stored.trim() !== "" : true;
        if (hasContent) {
          throw new ApiError(
            409,
            "destructive_clear_refused",
            "This save would replace a document that has content with an empty one. If that is deliberate, re-send with confirm_clear: true.",
          );
        }
      }
      if (typeof payload.rendered_html === "string") {
        savedHtml.set(`${unitId}-${role}`, payload.rendered_html);
      }
      return delay({
        head_hash: hashQuote(`${unitId}:${role}:save`).padStart(8, "0"),
        changed_sections: [
          { section_id: `sec-${role}-1`, seq: 2, change_kind: "modified" },
        ],
      });
    },
    async sectionVersions(sectionId) {
      return delay(mockSectionVersions(sectionId));
    },
    async compareSection(_sectionId, _aVersionId, _bVersionId) {
      return delay(articlePayoutDiff());
    },
    async listLinks(unitId) {
      return delay(buildLinks(unitId));
    },
    async repinLink(_linkId) {
      return delay(undefined);
    },
  };
}

/** A small canned link set: the "Auszahlungsgeschwindigkeit" article section
 *  still refers to an OLDER brief version (stale on the brief side); the others
 *  are fresh — so the "refers to brief v{n}" chip is exercisable with no backend. */
function buildLinks(_unitId: string): SectionLink[] {
  return [
    {
      id: 1,
      link_type: "brief_article",
      origin: "auto",
      article_section_id: 11,
      other_section_id: 21,
      other_role: "brief",
      article_heading: "Wie der Bonus funktioniert",
      other_heading: "Wie der Bonus funktioniert",
      other_pin_seq: 1,
      other_head_seq: 1,
      article_pin_seq: 1,
      article_head_seq: 1,
      stale_other: false,
      stale_article: false,
    },
    {
      id: 2,
      link_type: "brief_article",
      origin: "auto",
      article_section_id: 12,
      other_section_id: 22,
      other_role: "brief",
      article_heading: "Auszahlungsgeschwindigkeit",
      other_heading: "Auszahlungsgeschwindigkeit",
      other_pin_seq: 1,
      other_head_seq: 2,
      article_pin_seq: 1,
      article_head_seq: 1,
      stale_other: true,
      stale_article: false,
    },
  ];
}

// ── real adapter (design §2.8) — wired at W1 integration ─────────────────────
// RULE ZERO composition: the unit STRUCTURE + carved sections come from the new
// `GET /api/article-units/:id` terminal (handlers/article_units.zig); each
// resource's rendered content + comments + members + version history reuse the
// EXISTING `ElaborateApi` document endpoints (one document fetch path, no second
// renderer). brief/article are docz-rendered HTML painted by <DoczCanvas>;
// research is markdown — the article-unit read-model surfaces each section's raw
// `body` (`el.section_versions.body`), so its source is reconstructed from the
// carved section bodies (never the escaped rendered HTML through the md renderer).

/** GET /api/article-units/:id wire shape (models.ArticleUnitDetail — field names
 *  ARE the contract keys; std.json.Stringify emits them directly). */
interface BeSectionJson {
  id: number;
  document_id: number;
  resource_role: string;
  ord: number;
  heading: string;
  anchor_key: string;
  kind: string;
  content_hash: string;
  /** SERVER-STATED block span (design §1.4 hardening · `models.Section.block_lo/hi`):
   *  the section covers the flat `.docz-block` spine `[block_lo, block_hi)`.
   *  `block_hi < 0` denotes the last section ("to end"). Derived server-side from
   *  the carve's contiguous tiling so the client reads the region, never infers it. */
  block_lo: number;
  block_hi: number;
  /** The head version's source text (`el.section_versions.body`). Research is a
   *  single snippet section (empty `heading`) carrying the WHOLE markdown here. */
  body: string;
  head_version_id: number | null;
}
interface BeResourceJson {
  document_id: number;
  resource_role: string;
  format: string;
  title: string;
  content_sha256: string | null;
  section_count: number;
  sections: BeSectionJson[];
}
/** GET /api/article-units list row wire shape (models.ArticleUnit — field names
 *  ARE the contract keys). */
// ONE declaration of the row (RULE ZERO): `./articleAdminTypes` owns it, because
// the PATCH reply, the merge reply and the merge-candidate list return the very
// same `db/models.zig:ArticleUnit` projection. Two copies is how the two drift.
type BeUnitSummaryJson = ArticleUnitRowWire;
/**
 * The `POST /api/article-units` reply — the mirror of
 * `src/api/article_create_service.zig:Created`, which the handler writes under
 * the single envelope key `article_unit`. `unit` is the very same row shape the
 * library list returns, so a created project needs no client-invented fields.
 * Pinned against the Zig struct by `articleCreate.conformance.test.ts`.
 */
interface BeCreatedJson {
  article_unit_id: number;
  workspace_slug: string;
  slug: string;
  title: string;
  brief_doc_id: number;
  article_doc_id: number;
  research_doc_id: number;
  briefing_id: number | null;
  /** False ⇒ the brief could not be parsed and a fallback card was stored. */
  brief_rendered: boolean;
  brief_note: string;
  unit: BeUnitSummaryJson;
}
interface BeUnitDetailJson {
  id: number;
  workspace_slug: string;
  title: string;
  slug: string;
  brand: string;
  lang: string;
  status: string;
  section_level: number;
  /** N1 target word count — nullable on the wire (`models.ArticleUnitDetail`). */
  target_words: number | null;
  brief_doc_id: number;
  delivered_at: string | null;
  created_at: string;
  resources: BeResourceJson[];
}

const ROLE_ORDER: readonly ResourceRole[] = ["brief", "research", "article"];

function toDocStatus(s: string): DocStatus {
  return s === "delivered" || s === "superseded" || s === "draft"
    ? (s as DocStatus)
    : "draft";
}

/** GET one JSON resource from the real backend (session-cookie auth, same-origin,
 *  ApiError-normalised — mirrors realAdapter's GET contract for the article-unit
 *  terminals). */
async function getJson<T>(url: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: "include" });
  } catch (e) {
    throw new ApiError(0, "network_error", (e as Error).message);
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const d = data as { code?: unknown; message?: unknown } | null;
    throw new ApiError(
      res.status,
      d?.code != null ? String(d.code) : `http_${res.status}`,
      d?.message != null ? String(d.message) : res.statusText,
    );
  }
  return data as T;
}

/**
 * GET the NEGOTIATED BINARY representation of a document-shaped resource — the
 * typed block list as RAW bytes plus the `X-Document-*` provenance headers.
 *
 * ONE fetcher for both callers. The head document (`/api/documents/:id`) and an
 * archived version (`/api/documents/:id/versions/:vid`) are two resources with the
 * SAME representation, negotiated with the SAME `Accept`, and the server proves
 * the two byte streams are identical for identical stored forms
 * (`src/tests/e2e_version_representation.zig`). Two copies of this function would
 * be two places for a provenance header to drift, and a client that trusts
 * `X-Document-Source` on one route and not the other is back to guessing whether
 * it is looking at the stored IR or a reconstruction — which is precisely what the
 * header exists to answer.
 *
 * `versionId`, when given, is echoed onto the returned transport so a caller can
 * assert the bytes belong to the revision it asked for. It is checked against the
 * server's own `X-Document-Version` rather than trusted: a version viewer that
 * silently paints the head is invisible on a canvas, because there is no DOM to
 * inspect.
 */
async function fetchDocumentBinary(
  path: string,
  docId: string,
  versionId?: number,
): Promise<DoczBinaryTransport> {
  const url = new URL(path, window.location.origin).toString();
  let res: Response;
  try {
    res = await fetch(url, {
      credentials: "include",
      headers: { Accept: DOCUMENT_MEDIA_TYPE },
    });
  } catch (e) {
    throw new ApiError(0, "network_error", (e as Error).message);
  }
  if (!res.ok) {
    throw new ApiError(res.status, `http_${res.status}`, res.statusText);
  }
  const buf = await res.arrayBuffer();
  const served = res.headers.get("X-Document-Version");
  if (versionId != null && served !== null && served !== String(versionId)) {
    // REFUSED, not repaired. Painting a revision the caller did not ask for is
    // the one failure a canvas cannot show you, so it must never be silent.
    throw new ApiError(
      0,
      "version_mismatch",
      `asked for version ${versionId} but the server served version ${served}`,
    );
  }
  return {
    docId,
    bytes: new Uint8Array(buf),
    source: (res.headers.get("X-Document-Source") ?? "empty") as DoczBinarySource,
    hash: res.headers.get("X-Document-Hash") ?? "",
    contentType: res.headers.get("Content-Type") ?? "",
    baseVersionId: res.headers.get("X-Document-Base-Version") ?? "",
    ...(versionId != null ? { versionId } : {}),
  };
}

/** Read a non-HttpOnly cookie value by name — used to echo the signed CSRF
 *  double-submit token on writes (mirrors realAdapter.readCookie). */
function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const c = part.trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return undefined;
}

/** POST a JSON body to the real backend (session-cookie auth, same-origin,
 *  ApiError-normalised — the write counterpart of {@link getJson}).
 *
 *  CSRF (P2-13): the backend gates every state-changing `/api` write on a signed
 *  double-submit token — the SPA MUST echo the NON-HttpOnly `el_csrf` cookie in
 *  the `X-CSRF-Token` header or the save is rejected 403 (ELABORATE_CSRF_ENFORCE
 *  defaults ON). This is the SAME echo realAdapter.request() does for its writes;
 *  without it the whole-resource save (POST /api/documents/:id/content) fails. */
async function postJson<T>(url: string, body: unknown): Promise<T> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  const csrf = readCookie("el_csrf");
  if (csrf) headers["X-CSRF-Token"] = csrf;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new ApiError(0, "network_error", (e as Error).message);
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const d = data as { code?: unknown; message?: unknown } | null;
    throw new ApiError(
      res.status,
      d?.code != null ? String(d.code) : `http_${res.status}`,
      d?.message != null ? String(d.message) : res.statusText,
    );
  }
  return data as T;
}

/** GET /api/sections/:id/versions wire row (models.SectionVersion — field names
 *  ARE the contract keys). */
interface BeSectionVersionJson {
  id: number;
  section_id: number;
  seq: number;
  heading: string;
  content_sha256: string;
  change_kind: string;
  editor_name: string | null;
  created_at: string;
}

function toChangeKind(s: string): SectionVersion["change_kind"] {
  return s === "added" || s === "moved" || s === "unchanged"
    ? (s as SectionVersion["change_kind"])
    : "modified";
}

function mapSectionVersion(v: BeSectionVersionJson): SectionVersion {
  return {
    id: String(v.id),
    section_id: String(v.section_id),
    seq: v.seq,
    heading_text: v.heading,
    content_sha256: v.content_sha256,
    change_kind: toChangeKind(v.change_kind),
    editor_name: v.editor_name ?? "",
    created_at: v.created_at,
  };
}

const EMPTY_DIFF: DocumentDiff = {
  ops: [],
  counts: { equal: 0, inserted: 0, deleted: 0, moved: 0, changed: 0 },
};

/** GET /api/article-units/:id — one unit detail (models.ArticleUnitDetail). */
function fetchUnitDetail(unitId: string): Promise<BeUnitDetailJson> {
  const url = new URL(
    `/api/article-units/${encodeURIComponent(unitId)}`,
    window.location.origin,
  ).toString();
  return getJson<BeUnitDetailJson>(url);
}

/** GET /api/article-units?workspace=slug — the workspace's article-unit library. */
async function fetchUnitList(
  workspaceSlug: string,
): Promise<BeUnitSummaryJson[]> {
  const url = new URL("/api/article-units", window.location.origin);
  if (workspaceSlug) url.searchParams.set("workspace", workspaceSlug);
  return getJson<BeUnitSummaryJson[]>(url.toString());
}

function mapSummary(u: BeUnitSummaryJson): ArticleUnitSummary {
  return {
    id: String(u.id),
    workspace_slug: u.workspace_slug,
    title: u.title,
    slug: u.slug,
    brand: u.brand,
    lang: u.lang,
    status: toDocStatus(u.status),
    section_level: u.section_level,
    brief_doc_id: u.brief_doc_id,
    resource_count: u.resource_count,
    delivered_at: u.delivered_at,
    created_at: u.created_at,
  };
}

function mapSection(s: BeSectionJson): Section {
  return {
    id: String(s.id),
    role: s.resource_role as ResourceRole,
    ord: s.ord,
    heading: s.heading,
    anchor_key: s.anchor_key,
    head_version_id: s.head_version_id != null ? String(s.head_version_id) : "",
    content_hash: s.content_hash,
    // SERVER-STATED region (design §1.4): a non-negative `block_hi` is the explicit
    // exclusive upper bound; `< 0` means the last section runs to the body end
    // (the hover-handle layer then maps its region to the last DOM block).
    block_lo: s.block_lo,
    block_hi: s.block_hi >= 0 ? s.block_hi : undefined,
    // Per-section version stream (GET /api/sections/:id/versions) is Wave 2.
    versions: [],
    stale: false,
  };
}

/** Reconstruct research markdown from the carved sections. The read-model
 *  surfaces each section's `body` (`el.section_versions.body`) — the raw markdown
 *  source — so we render THAT, not the rendered HTML (the markdown renderer
 *  escapes HTML, which would show literal tags). A research SNIPPET carries the
 *  whole document under an EMPTY heading, so its body is emitted verbatim (never a
 *  bogus `## ` heading); a heading-bearing section is re-titled above its body. */
export function researchMarkdownFromSections(res: BeResourceJson): string {
  return res.sections
    .map((s) => {
      const heading = s.heading.trim();
      const body = (s.body ?? "").trim();
      if (heading.length === 0) return body; // snippet: body IS the whole markdown
      return body.length ? `## ${heading}\n\n${body}` : `## ${heading}`;
    })
    .filter((chunk) => chunk.length > 0)
    .join("\n\n");
}

/**
 * The real article-unit adapter. Selected when VITE_USE_MOCK is off; composes the
 * live ElaborateApi so the review layer (comments / members / versions) reuses the
 * exact endpoints DocumentViewer already drives.
 */
/**
 * Lower a server `restored_from_version_id` into the `VersionTick.revertOf` the
 * timeline renders, or nothing at all.
 *
 * Three cases, and the middle one is the trap:
 *   * absent / 0        → an ordinary revision, NO `revertOf` key. `undefined`
 *                         and `0` must behave identically or a legacy server
 *                         (which omits the field) turns every revision into a
 *                         "revert of undefined".
 *   * present, in list  → `{ versionId, label }` — fully renderable.
 *   * present, absent   → `{ versionId }` with NO label. The source revision can
 *                         legitimately be missing: `listVersions` drops
 *                         unreleased rows for a non-internal viewer. Degrade the
 *                         LABEL, never the FACT — dropping `revertOf` here would
 *                         tell that viewer the revision is an ordinary edit.
 */
function revertOfFrom(
  sourceId: number | undefined,
  labelById: ReadonlyMap<number, string>,
): { revertOf?: { versionId: string; label?: string } } {
  if (!sourceId) return {};
  const label = labelById.get(sourceId);
  return { revertOf: label ? { versionId: String(sourceId), label } : { versionId: String(sourceId) } };
}

export function createArticleUnitsReal(base: ElaborateApi): ArticleUnitsApi {
  return {
    async listArticleUnits(workspaceSlug) {
      const list = await fetchUnitList(workspaceSlug);
      return list.map(mapSummary);
    },

    async createArticleUnit(workspaceSlug, input) {
      // ONE request creates the project, its 3 role-tagged resources and (when
      // asked) the agent work item — atomically, server-side. The client sends
      // the brief bytes inline (base64) exactly as the document upload does, so
      // there is no second transport and no multipart parser to hand-roll.
      const url = new URL("/api/article-units", window.location.origin).toString();
      // EVERY key below is a declared `CreateBody` field. Do not add one that is
      // not — Zig's decoder ignores an unknown key silently, so an extra name
      // here is a field that never arrives and a 201 that means nothing.
      const res = await postJson<{ article_unit?: BeCreatedJson }>(url, {
        workspace: workspaceSlug,
        title: input.title,
        slug: input.slug ?? "",
        brand: input.brand ?? "",
        lang: input.lang ?? "",
        brief_format: input.brief_format,
        brief_filename: input.brief_filename ?? "",
        brief_content_b64: input.brief_content_b64,
        enqueue_agent_work: input.enqueue_agent_work,
      });
      // The handler's envelope is `{"article_unit": Created}`. Reading a key the
      // server does not write used to throw a raw TypeError inside `mapSummary`
      // and surface as "Could not create the article." on a 201 that had really
      // created the project. A drifted envelope is now a typed, named failure.
      const created = res.article_unit;
      if (!created || !created.unit) {
        throw new ApiError(
          502,
          "create_shape_drift",
          "The server created the project but replied in a shape this app cannot read.",
        );
      }
      return {
        // The SAME row the library list returns, decoded by the SAME mapper —
        // nothing about the new project is invented client-side.
        unit: mapSummary(created.unit),
        // `brief_rendered` is the server's own verdict on the ingest. Absent ⇒
        // treat as rendered, so an older server is not read as a failure.
        ingest_ok: created.brief_rendered !== false,
        note: created.brief_note ?? "",
      };
    },

    // ── lifecycle: rename / delete / merge ───────────────────────────────
    // All three go through `base` — realAdapter's `request()`, the ONE transport
    // with the stale-CSRF refresh-and-retry-once recovery. NOT the local
    // `postJson` below, which echoes the token but cannot recover from a rotated
    // one; a delete that 403s irrecoverably in a long-lived tab is not a delete.

    async renameArticleUnit(unitId, patch) {
      // Only the keys the caller NAMED go on the wire. Absent means "keep the
      // stored value" server-side, so sending `brand: undefined` as an explicit
      // null would blank a brand the user never touched. Every key below is a
      // declared `PatchBody` field — `articleAdmin.conformance.test.ts` proves it.
      const body: ArticleUnitPatchWire = {};
      if (patch.title !== undefined) body.title = patch.title;
      if (patch.slug !== undefined) body.slug = patch.slug;
      if (patch.brand !== undefined) body.brand = patch.brand;
      if (patch.lang !== undefined) body.lang = patch.lang;
      if (patch.target_words !== undefined) body.target_words = patch.target_words;
      if (patch.clear_target_words !== undefined) {
        body.clear_target_words = patch.clear_target_words;
      }
      const res = await base.patchArticleUnit(unitId, body);
      const row = res?.article_unit;
      if (!row) {
        // A drifted envelope must fail LOUDLY. Reading a key the server does not
        // write used to throw a raw TypeError inside the mapper and surface as a
        // generic failure on a 200 that had really renamed the project.
        throw new ApiError(
          502,
          "patch_shape_drift",
          "The server renamed the project but replied in a shape this app cannot read.",
        );
      }
      return mapSummary(row);
    },

    async deleteArticleUnit(unitId, opts) {
      // QUERY parameters, never a body — and `confirm` is OMITTED entirely
      // unless the caller supplied one, so a `detach` delete puts no
      // confirmation on the wire at all.
      return base.deleteArticleUnit(unitId, {
        documents: opts.documents,
        ...(opts.dry_run ? { dry_run: "1" } : {}),
        ...(opts.confirm ? { confirm: opts.confirm } : {}),
      });
    },

    async listMergeCandidates(unitId) {
      const rows = await base.listArticleMergeCandidates(unitId);
      return (rows ?? []).map(mapSummary);
    },

    async mergeArticleParts(unitId, input) {
      const body: MergePartsWire = { dry_run: input.dry_run };
      if (input.brief_from_unit_id != null) {
        body.brief_from_unit_id = input.brief_from_unit_id;
      }
      if (input.article_from_unit_id != null) {
        body.article_from_unit_id = input.article_from_unit_id;
      }
      if (input.research_from_unit_id != null) {
        body.research_from_unit_id = input.research_from_unit_id;
      }
      const res = await base.mergeArticleParts(unitId, body);
      if (!res || !Array.isArray(res.parts)) {
        throw new ApiError(
          502,
          "merge_shape_drift",
          "The server answered the merge in a shape this app cannot read.",
        );
      }
      return res;
    },

    async getArticleUnit(_slug, unitId) {
      const detail = await fetchUnitDetail(unitId);
      const resources = {} as Record<ResourceRole, ArticleResource>;
      for (const role of ROLE_ORDER) {
        const r = detail.resources.find((x) => x.resource_role === role);
        if (!r) continue;
        const docId = String(r.document_id);
        const doc = await base.getDocument(docId);
        const isHtml = r.format !== "md";
        resources[role] = {
          role,
          doc_id: docId,
          title: r.title,
          format: isHtml ? "html" : "md",
          // Head seq: accurate per-resource ticks come from resourceVersions();
          // the header chip shows v1 for a freshly-carved W1 resource.
          seq: 1,
          content_hash: r.content_sha256 ?? "",
          sections: r.sections.map(mapSection),
          stale: false,
          ...(isHtml
            ? { rendered_html: doc.rendered_html ?? "" }
            : { body_md: researchMarkdownFromSections(r) }),
        };
      }
      return {
        id: String(detail.id),
        slug: detail.slug,
        title: detail.title,
        brand: detail.brand,
        lang: detail.lang,
        status: toDocStatus(detail.status),
        section_level: detail.section_level,
        targetWords: detail.target_words ?? undefined,
        resources,
      };
    },

    async listComments(unitId) {
      const detail = await fetchUnitDetail(unitId);
      const out: WorkbenchComment[] = [];
      for (const r of detail.resources) {
        const role = r.resource_role as ResourceRole;
        const comments = await base.listComments(String(r.document_id));
        for (const c of comments) {
          // W3: the comments read-model now carries the §1.7 routing cols +
          // the version-aware anchor state (el.comment_anchors). Prefer the
          // server's home_resource/pairing_side; fall back to the resource role
          // for an unanchored/legacy comment.
          const raw = c as Comment & {
            home_resource?: string;
            pairing_side?: PairingSide;
            anchor_state?: RebaseState;
            anchored_seq?: number;
          };
          out.push({
            ...c,
            home_resource:
              (raw.home_resource as ResourceRole) || role,
            pairing_side: raw.pairing_side ?? "none",
            anchor_state: raw.anchor_state,
            anchored_seq: raw.anchored_seq,
          });
        }
      }
      return out;
    },

    // §2.5 dual-gutter authoring: the three comment writes reuse the EXACT
    // endpoints DocumentViewer drives (POST /comments, /replies, /status) — one
    // write path, no second review implementation (RULE ZERO). The created
    // comment posts to the pane's document, so the backend stamps its
    // `home_resource` from that document's `resource_role` (routing to the pane's
    // gutter). Persistence is the server's — a reload re-reads the row.
    async createComment(docId, body) {
      return base.createComment(docId, body);
    },
    async replyComment(commentId, body) {
      return base.replyToComment(commentId, body);
    },
    async setCommentStatus(commentId, status) {
      return base.setCommentStatus(commentId, status);
    },
    async editComment(commentId, body) {
      return base.editComment(commentId, body);
    },
    async deleteComment(commentId) {
      return base.deleteComment(commentId);
    },
    async setCommentVisibility(commentId, body) {
      return base.setCommentVisibility(commentId, body);
    },
    async toggleReaction(commentId, emoji, add) {
      return base.toggleReaction(commentId, emoji, add);
    },

    async listMembers(unitId) {
      const detail = await fetchUnitDetail(unitId);
      const anchor =
        detail.resources.find((r) => r.resource_role === "article") ??
        detail.resources[0];
      if (!anchor) return [];
      return base.listMembers(String(anchor.document_id));
    },

    async resourceVersions(unitId, role) {
      const detail = await fetchUnitDetail(unitId);
      const r = detail.resources.find((x) => x.resource_role === role);
      if (!r) return [];
      const versions = await base.listVersions(String(r.document_id));
      // Oldest first. THE `|| a.id - b.id` IS LOAD-BEARING, not tidiness.
      //
      // A revert writes TWO rows — the pre-revert BACKUP of the head, then the
      // revert itself — inside ONE transaction, and PostgreSQL's `now()` is
      // TRANSACTION-START time, so both carry a byte-identical `created_at`.
      // Sorting on the timestamp alone leaves that pair in arrival order (sort is
      // stable), which is the server's `created_at DESC, id DESC` — revert first,
      // backup second — so the BACKUP lands last and collects `isHead` and the
      // highest `vN`. The timeline then crowns a snapshot of the content that was
      // reverted AWAY from, and paints the revert marker one tick below the head.
      //
      // Ordering by id within the tie is also order-INDEPENDENT: it does not
      // quietly depend on the server's ORDER BY, which a future query change
      // could flip without a single test noticing. Ids are monotone, so "later
      // id" is "later row" even when the clock cannot tell them apart.
      const sorted = [...versions].sort(
        (a, b) => a.created_at.localeCompare(b.created_at) || a.id - b.id,
      );
      // id → human label, from THIS list, so a revert can say "revert of v2"
      // instead of "revert of 8817". Built once rather than per-tick: the list
      // is the only place the seq→id mapping exists (seq is positional, not a
      // server field).
      const labelById = new Map<number, string>(
        sorted.map((v, i) => [v.id, `v${i + 1}`]),
      );
      return sorted.map((v, i) => ({
        versionId: String(v.id),
        seq: i + 1,
        label: `v${i + 1}`,
        isHead: i === sorted.length - 1,
        createdAt: v.created_at,
        // Surface the version editor (el.doc_versions.editor_name) so the
        // in-header VersionSwitcher can show "v7 · 2026-07-24 · Dom" (design §5).
        author: v.editor_name || undefined,
        ...revertOfFrom(v.restored_from_version_id, labelById),
      }));
    },

    async revertResource(unitId, role, versionId, note) {
      const detail = await fetchUnitDetail(unitId);
      const r = detail.resources.find((x) => x.resource_role === role);
      if (!r) throw new ApiError(404, "resource_not_found", "No such resource.");
      return base.restoreVersion(String(r.document_id), Number(versionId), note);
    },

    async compareResource(unitId, role, aVersionId, bVersionId) {
      // W2: server-computed whole-resource diff (spec D3 · docz_diff over the two
      // el.doc_versions IR snapshots). Resolve the role's document, then hit the
      // versioned compare terminal; the server emits the exact DocumentDiff shape.
      if (!aVersionId || !bVersionId) return EMPTY_DIFF;
      const detail = await fetchUnitDetail(unitId);
      const r = detail.resources.find((x) => x.resource_role === role);
      if (!r) return EMPTY_DIFF;
      const url = new URL(
        `/api/documents/${r.document_id}/versions/${encodeURIComponent(
          aVersionId,
        )}/compare/${encodeURIComponent(bVersionId)}`,
        window.location.origin,
      ).toString();
      return getJson<DocumentDiff>(url);
    },

    async getDoczDocument(docId) {
      // Spec D1: the server re-ingests rendered_html into the canonical docz IR
      // and emits the block transport (Zig owns the IR — no client fromHtml).
      const url = new URL(
        `/api/documents/${encodeURIComponent(docId)}/docz`,
        window.location.origin,
      ).toString();
      return getJson<DoczTransport>(url);
    },

    async getDoczBinary(docId) {
      // ONE resource, negotiated representation. RAW bytes: no JSON envelope, no
      // base64. Provenance rides on the headers, so a legacy-HTML reconstruction
      // can never be mistaken for the stored typed IR.
      return fetchDocumentBinary(
        `/api/documents/${encodeURIComponent(docId)}`,
        String(docId),
      );
    },

    async getVersionDoczBinary(docId, versionId) {
      // An ARCHIVED VERSION is a document, so it has the document's
      // representations: the SAME `Accept`, the SAME raw block-DTO bytes, the SAME
      // provenance headers, through the SAME fetcher. The server proves the two
      // wires are byte-identical (`src/tests/e2e_version_representation.zig`).
      return fetchDocumentBinary(
        `/api/documents/${encodeURIComponent(docId)}/versions/${encodeURIComponent(
          String(versionId),
        )}`,
        String(docId),
        versionId,
      );
    },

    async getBrandedBodyDto(docId) {
      // The server prepares the body EXACTLY as the branded PDF (ingest → explode
      // raw_html → Cerebr8 styles) and returns the docz block-DTO wire (base64).
      const url = new URL(
        `/api/documents/${encodeURIComponent(docId)}/branded-docz`,
        window.location.origin,
      ).toString();
      const res = await getJson<{ title?: string; body_dto_b64: string }>(url);
      return { title: res.title ?? "", bodyDto: base64ToBlockDtoBytes(res.body_dto_b64) };
    },

    async saveResource(unitId, role, payload) {
      // Spec D1: resolve the role's document, then POST the whole-resource save.
      // The server re-carves + advances only the changed per-section streams.
      const detail = await fetchUnitDetail(unitId);
      const r = detail.resources.find((x) => x.resource_role === role);
      if (!r) {
        throw new ApiError(404, "not_found", "No such resource for this unit.");
      }
      const url = new URL(
        `/api/documents/${r.document_id}/content`,
        window.location.origin,
      ).toString();
      return postJson<ResourceSaveResult>(url, payload);
    },

    async sectionVersions(sectionId) {
      const url = new URL(
        `/api/sections/${encodeURIComponent(sectionId)}/versions`,
        window.location.origin,
      ).toString();
      const rows = await getJson<BeSectionVersionJson[]>(url);
      return rows.map(mapSectionVersion);
    },

    async compareSection(sectionId, aVersionId, bVersionId) {
      if (!aVersionId || !bVersionId) return EMPTY_DIFF;
      const url = new URL(
        `/api/sections/${encodeURIComponent(sectionId)}/compare`,
        window.location.origin,
      );
      url.searchParams.set("from", aVersionId);
      url.searchParams.set("to", bVersionId);
      return getJson<DocumentDiff>(url.toString());
    },

    async listLinks(unitId) {
      const url = new URL(
        `/api/article-units/${encodeURIComponent(unitId)}/links`,
        window.location.origin,
      ).toString();
      return getJson<SectionLink[]>(url);
    },

    async repinLink(linkId) {
      const url = new URL(
        `/api/section-links/${encodeURIComponent(String(linkId))}/repin`,
        window.location.origin,
      ).toString();
      await postJson<{ id: number; repinned: boolean }>(url, {});
    },
  };
}

/** VITE_USE_MOCK selector (identical semantics to api/index.ts) — default REAL so
 *  a production build (no env) can NEVER ship mocks; the fixtures are opt-IN via
 *  an explicitly-truthy VITE_USE_MOCK ("1"/"true"). */
/// STATIC FOLDING (load-bearing — see the note in `api/index.ts`): written flat
/// (no `?.`) so Vite's literal substitution lets Rollup fold the selector to a
/// constant and drop `createArticleUnitsMock` + its fixture corpus from a real
/// build. The `?.` form kept this a runtime value and shipped the mocks.
const MOCK_FLAG = import.meta.env.VITE_USE_MOCK;
const USE_MOCK_ARTICLES = MOCK_FLAG === "1" || MOCK_FLAG === "true";

/**
 * The article-unit API singleton the workbench imports. REAL by default (hits
 * `/api/article-units`); the in-memory fixtures are selected ONLY by an
 * explicitly-truthy VITE_USE_MOCK — the shapes above are the contract either way.
 */
export const articleApi: ArticleUnitsApi = USE_MOCK_ARTICLES
  ? createArticleUnitsMock()
  : createArticleUnitsReal(createRealAdapter());
